'use client';

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface RequestTimeoutSectionProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  size?: 'md' | 'sm';
  helperIcon?: React.ReactNode;
}

const Container = styled.div`
  position: relative;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const Help = styled.p`
  margin: 4px 0 12px 0;
  font-size: 12px;
  color: #6b7280;
`;

const SliderWrapper = styled.div<{ $size: 'md' | 'sm' }>`
  position: relative;
  padding: ${props => props.$size === 'sm' ? '0 0' : '12px 0'};
`;

const Slider = styled.input.attrs<{ value: number }>(props => ({
  style: {
    background: `linear-gradient(to right, #FB6F2A 0%, #D33324 ${((props.value - 1) / 59) * 100}%, #e5e7eb ${((props.value - 1) / 59) * 100}%, #e5e7eb 100%)`
  }
}))`
  width: 100%;
  height: 4px;
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border: 1px solid #D9D9DC;
    background: #ffffff;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: #0000001A;
    transition: all 0.2s ease;

  
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: 1px solid #D9D9DC;
    background: #ffffff;
    border-radius: 50%;
    box-shadow: #0000001A;
    background: #FB6F2A;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;

    &:hover {
  background: linear-gradient(180deg, #FB6F2A 0%, #D33324 100%);
    border-color: #D33324;
      transform: scale(1.1);
    }
  }
`;

const ValuePopup = styled.div<{ $left: number; $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 12px);
  left: ${props => props.$left}%;
  transform: translateX(-50%);
  background: linear-gradient(180deg, #FB6F2A 0%, #D33324 100%);
  color: white;
  padding: 3px ;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  width: 30px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-family: 'Open Sans', sans-serif;

  /* Speech bubble tail */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #D33324;
  }

  /* Number styling */
  span:first-child {
    font-size: 12px;
    font-weight: 400;
    line-height: 1;
  }

  /* Unit styling */
  span:last-child {
    font-size: 14px;
    font-weight: 500;
    margin-left: 2px;
  }
`;

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
  color: #6e6e6e;
  font-size: 12px;
`;

export const RequestTimeoutSection: React.FC<RequestTimeoutSectionProps> = ({
  value,
  onChange,
  className,
  size = 'md',
  helperIcon,
}) => {
  const [isInteracting, setIsInteracting] = useState(false);
  const [thumbPosition, setThumbPosition] = useState(0);
  const sliderRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Calculate thumb position based on value
  useEffect(() => {
    const min = 1;
    const max = 60;
    const percentage = ((value - min) / (max - min)) * 100;
    setThumbPosition(percentage);
  }, [value]);

  const handleMouseDown = () => {
    setIsInteracting(true);
  };

  const handleMouseUp = () => {
    setIsInteracting(false);
  };

  const handleMouseEnter = () => {
    setIsInteracting(true);
  };

  const handleMouseLeave = () => {
    setIsInteracting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value));
  };

  return (
    <Container className={className}>
      <Label>
        Request timeout
        {helperIcon}
      </Label>
      {size === 'md' && <Help>The request timeout is {value} seconds.</Help>}
      <SliderWrapper 
        $size={size} 
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ValuePopup 
          $left={thumbPosition} 
          $visible={isInteracting}
        >
          <span>{value}</span><span>s</span>
        </ValuePopup>
        <Slider
          ref={sliderRef}
          type="range"
          min={1}
          max={60}
          step={1}
          value={value}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
        />
      </SliderWrapper>
      <Labels>
        <span>1s</span>
        <span>15s</span>
        <span>30s</span>
        <span>45s</span>
        <span>60s</span>
      </Labels>
    </Container>
  );
};

export default RequestTimeoutSection;

